import api from "./api";

/**
 * Issue #851 (epic #847, voice wave V2) — pure helpers and I/O for the one
 * dictation component every long box uses (`components/kit/DictationControl.jsx`).
 * Kept as a plain, React-free module (this repo's own testability
 * convention — see `lib/logSavedStep.js`'s own header) so every branch here
 * is a Jest test with no DOM at all.
 *
 * Spec: `jobs/cricket-compass/feedback/voice-design-2026-09-25.md`
 * (design overlay, tag `design-baseline-round-6-voice`), §§ 2, 7, 9.
 * D112: audio is transcribed then discarded, never stored — nothing here
 * writes to IndexedDB, the Cache API or a service worker; the only
 * `localStorage` key is the once-per-device explainer flag below (D116).
 */

const EXPLAINER_STORAGE_KEY = "nw_voice_explainer_seen";

/** True only when this browser can record at all (spec § 1, "not-offered"). */
export function canRecord() {
  return (
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined" &&
    !!(window.navigator && window.navigator.mediaDevices && window.navigator.mediaDevices.getUserMedia)
  );
}

/**
 * The first supported mime type, spec § 7 ("Chrome: WebM/Opus. Safari:
 * MP4/AAC. Both are accepted as recorded, so there is no conversion.") —
 * `""` lets `MediaRecorder` fall back to the browser default rather than
 * refuse to record on a browser that supports none of the three literally.
 */
export function pickMimeType() {
  const candidates = ["audio/webm;codecs=opus", "audio/mp4", "audio/webm"];
  const isTypeSupported =
    typeof window !== "undefined" && window.MediaRecorder && typeof window.MediaRecorder.isTypeSupported === "function"
      ? window.MediaRecorder.isTypeSupported
      : null;
  if (!isTypeSupported) return "";
  for (const c of candidates) {
    if (isTypeSupported(c)) return c;
  }
  return "";
}

/**
 * Chunked, 0x8000 bytes per `String.fromCharCode.apply` — the issue's own
 * "pattern to copy" from the spike recorder. A single `apply` over a whole
 * 90-second clip's buffer overflows the call stack; chunking is the fix,
 * not a micro-optimisation.
 */
function readAsArrayBuffer(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
    reader.onload = () => resolve(reader.result);
    reader.readAsArrayBuffer(blob);
  });
}

export async function blobToBase64(blob) {
  // `Blob.prototype.arrayBuffer` where it exists (every real browser this
  // ships to); `FileReader` otherwise — jsdom's own `Blob` does not
  // implement `arrayBuffer()`, and this same function runs under both.
  const buf = typeof blob.arrayBuffer === "function" ? await blob.arrayBuffer() : await readAsArrayBuffer(blob);
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(s);
}

/**
 * `GET /voice/status`. Any error — network, 4xx, 5xx — resolves to
 * `{ available: false }` and never throws: this is polled on mount and on
 * every successful transcript, and a screen must never break because the
 * status probe itself failed.
 */
export async function getVoiceStatus() {
  try {
    const res = await api.get("/voice/status");
    return res && res.data ? res.data : { available: false };
  } catch {
    return { available: false };
  }
}

function mapTranscribeError(e) {
  // An abort (our own 20s give-up, D117/GIVE_UP_AFTER_MS) or axios's own
  // request timeout both mean the same thing to the player: it took too
  // long. Neither carries a `response`.
  if (e && (e.name === "CanceledError" || e.code === "ERR_CANCELED" || e.name === "AbortError" || e.code === "ECONNABORTED")) {
    return { kind: "too_slow" };
  }
  const status = e && e.response ? e.response.status : null;
  if (status == null) return { kind: "network" };
  const code = e.response.data && e.response.data.code;
  if (status === 413) return { kind: "too_long" };
  if (status === 429 && code === "voice_daily_limit") return { kind: "daily_limit" };
  if (status === 429 && code === "voice_rate_limited") return { kind: "rate_limited" };
  if (status === 403 || status === 503) return { kind: "unavailable" };
  return { kind: "server" };
}

/**
 * `POST /voice/transcribe`. `seconds` is the client's own measured clip
 * length (never trusted server-side beyond a declared-duration hint —
 * #849's own concern, not this file's). `signal` is an `AbortController`
 * signal the caller aborts at `GIVE_UP_AFTER_MS` (D117).
 */
export async function transcribe(blob, seconds, signal) {
  try {
    const audio_b64 = await blobToBase64(blob);
    const res = await api.post(
      "/voice/transcribe",
      { audio_b64, mime: blob.type, declared_duration_s: seconds },
      { timeout: 20_000, signal }
    );
    const text = res && res.data && typeof res.data.text === "string" ? res.data.text : "";
    if (text.trim() === "") return { kind: "empty" };
    return { kind: "text", text };
  } catch (e) {
    return mapTranscribeError(e);
  }
}

/**
 * Q3 -> D121: spoken words always go after the existing text, on a new
 * line, never at the cursor and never replacing it.
 *
 * `maxLength` (D126, issue #851's executor spec) — when the appended
 * result would exceed the box's own limit (8,000 for the saved step's
 * reflection), the result is cut to fit and `overflowed: true` is
 * returned. `inserted` always names the exact suffix Undo must remove —
 * including the separating newline when one was added — so
 * `undoAppend` can restore the box to precisely its pre-talk state, cut
 * or not.
 */
export function appendHeard(existing, heard, maxLength = Infinity) {
  const trimmedExisting = existing.trim() === "" ? "" : existing.replace(/\s+$/, "");
  const prefixLen = trimmedExisting.length;
  const full = prefixLen === 0 ? heard : `${trimmedExisting}\n${heard}`;
  if (full.length <= maxLength) {
    return { value: full, inserted: full.slice(prefixLen), overflowed: false };
  }
  const value = full.slice(0, maxLength);
  const inserted = value.slice(Math.min(prefixLen, value.length));
  return { value, inserted, overflowed: true };
}

/**
 * Removes exactly `inserted` from the end of `value`. A no-op — `value`
 * returned unchanged — when the player has since typed over the end, so
 * Undo never eats text it didn't add.
 */
export function undoAppend(value, inserted) {
  if (!inserted) return value;
  if (value.endsWith(inserted)) return value.slice(0, value.length - inserted.length);
  return value;
}

/** D116 — shown once per device, and again after a denial. Storage that
 * throws (a private window, policy-blocked storage) is treated as "not
 * seen" rather than crashing the screen — same fail-open shape
 * `featureFlags.js`'s own localStorage read uses. */
export function explainerSeen() {
  try {
    return window.localStorage.getItem(EXPLAINER_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/** Marked only after a grant (never on a mere press) — see `DictationControl`. */
export function markExplainerSeen() {
  try {
    window.localStorage.setItem(EXPLAINER_STORAGE_KEY, "true");
  } catch {
    // Storage blocked or full: the explainer simply shows again next time.
    // Never let this take the recording flow down (D116).
  }
}
